import { useCallback, useEffect } from "react";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { AnimateCollapse } from "@/shared/ui";
import { AccountState, selectedCharIdSelector, setSelectedCharId } from "@/domains/account/slice";
import useGetCharacters from "@/domains/account/hooks/useGetCharacters";
import useRestoreCharacter from "@/domains/account/hooks/useRestoreCharacter";
import AccountCharCreateContainer from "@/domains/account/containers/char/AccountCharCreateContainer.tsx";
import AccountCharLockedContainer from "@/domains/account/containers/char/AccountCharLockedContainer";
import AccountCharPlayContainer from "@/domains/account/containers/char/AccountCharPlayContainer.tsx";
import AccountCharSettingsContainer from "@/domains/account/containers/char/AccountCharSettingsContainer.tsx";
import AccountChar from "@/domains/account/components/char/AccountChar.tsx";

type Props = {
  isLocked?: boolean;
  character?: AccountState["characters"][0];
};

export default function AccountCharContainer({ isLocked, character }: Props) {
  const { restoreHandler, isPending, isCompleted } = useRestoreCharacter();
  const { getCharacters } = useGetCharacters();
  const dispatch = useAppDispatch();
  const selectedCharId = useAppSelector(selectedCharIdSelector);
  const isSelected = selectedCharId === character?.id;

  const onCollapse = useCallback(() => {
    if (character) {
      dispatch(setSelectedCharId(character.id));
    }
  }, [dispatch, character]);

  const onRestore = useCallback(() => {
    if (character) {
      restoreHandler({ characterId: character.id });
    }
  }, [character, restoreHandler]);

  useEffect(() => {
    if (isCompleted) {
      getCharacters();
    }
  }, [getCharacters, isCompleted]);

  return (
    <div className="text-left relative z-1 min-h-72.5 w-full flex flex-col group card">
      {isLocked ? (
        <AccountCharLockedContainer />
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {character ? (
            // eslint-disable-next-line react/jsx-no-useless-fragment

            <>
              <AccountChar
                character={character}
                onClick={isSelected || character.isDeleted ? undefined : onCollapse}
                onRestore={character.isDeleted ? onRestore : undefined}
                pending={isPending}
              />
              <AnimateCollapse open={isSelected}>
                <div className="flex flex-col gap-2 mt-4">
                  <AccountCharPlayContainer />
                  <AccountCharSettingsContainer />
                </div>
              </AnimateCollapse>
            </>
          ) : (
            <AccountCharCreateContainer />
          )}
        </>
      )}
    </div>
  );
}
